import { datadogRum } from '@datadog/browser-rum'
import {
  ActivePromotion,
  IFreegoods,
  ISkuQuantity,
  PromotionType,
} from '../../graphQL/commons'
import {
  ICartProduct,
  IConditionAction,
  Condition,
  IConfig,
  ITransformCartProductsParams,
} from '../../interfaces'
import { getActivePromotionsFields } from '../common/getPromotionFields'
import { generateDataDogContext } from '../dataDog'
import { promotionsMap } from './getPromotionDetailsByType'
import { transformProductArray } from './setPromotionUiDetailsCart'

/**
 * Prefix for SKUs of free-good products added for the GMV (Total Order) case.
 */
export const PREFIX_GMV_FREE_PRODUCT = 'GMV_FREE_PRODUCT_'

/**
 * Key for products dictionary on parent-child mapping objects if no specific packageName used.
 */
export const PRODUCTS_PROP_NAME = 'products'

/**
 * Layout for mapping parent SKUs/packages to freegood child lists.
 */
type ProductsByPackage = Record<string, ICartProduct[] | undefined>
type ParentChildMap = Record<string, ProductsByPackage | undefined>

/**
 * Determines if the given SKU entry matches a given product (by SKU and packageName).
 */
const isSkuQuantitySameAsProduct = (
  { sku, packageName }: ISkuQuantity,
  { sku: productSku, package: productPackage }: ICartProduct
) =>
  sku === productSku && (productPackage?.name ?? null) === packageName

/**
 * Picks the consumed entry naming the cart line a freegood hangs off.
 *
 * `consumed` names the skus that earned the freegood. Payloads seen so far arrive
 * in no particular order and some include the granted sku itself; neither is a
 * documented backend guarantee, so treat both as observed shapes rather than
 * contract.
 *
 * Filing the freegood under a sku whose cart line is not rendered loses it:
 * `getCartProductsWithSubitems` (below in this file) drops lines failing its
 * `quantity > 0` filter, and on `options.freegoodsAsSubitem` storefronts a purely
 * promotional line is left at 0 by `sanitizeCartProducts`. Preferring an entry
 * that resolves to a rendered line therefore keeps the freegood attached to
 * something the customer can see.
 *
 * The guarantee here is deliberately a negative one: an entry is never skipped
 * for naming the granted sku, so a promotion that legitimately rewards a sku the
 * customer also paid for keeps its gift on that line. Among several entries that
 * all resolve to rendered lines, the first in `consumed` wins — array position is
 * the tie-break, not intent.
 *
 * Two couplings to keep in mind when editing. The `quantity > 0` test below
 * duplicates `conditionsProductosFirstLevel`, and the two must stay in step or a
 * freegood gets filed under a line that the filter later drops. And the fallback
 * to the first named entry is load-bearing despite resolving to an unrendered
 * line: `getFreeGoodsList` flattens every key of the mapping regardless of
 * whether it renders, so dropping the fallback would remove the gift from the
 * `freegoodAsCartGroup` list entirely.
 */
const getConsumedParent = (
  freegood: IFreegoods,
  cartProducts: ICartProduct[],
): ISkuQuantity | undefined => {
  const namedConsumed = freegood.consumed?.filter(({ sku }) => !!sku) ?? []

  const renderedParent = namedConsumed.find((consumedItem) =>
    cartProducts.some(
      (cartProduct) =>
        isSkuQuantitySameAsProduct(consumedItem, cartProduct) &&
        cartProduct.quantity > 0,
    ),
  )

  return renderedParent ?? namedConsumed[0]
}

/**
 * How many rendered skus travel with an orphan report. See the call site for why
 * this is bounded.
 */
const RENDERED_SKUS_REPORT_LIMIT = 20

/**
 * Reports freegoods left over after every rendered line has claimed its children.
 *
 * A leftover is proof of loss rather than a guess: the mapping is drained entry by
 * entry as lines render, so anything still present names a parent that the customer
 * will never see, and the gift silently disappears. That silence is what made this
 * class of bug expensive to find, so it is worth one aggregated event.
 *
 * Emitted once per call with the whole list, not once per freegood, so a broken
 * payload does not cost one event per gift. A cart response still yields several
 * calls: `setPromotionUiDetailsCartSubitems` runs once over the flat `items` array
 * and once per cart group (`onCartResponse.ts` and `transformGroups`), so one lost
 * gift costs `1 + groups` events per cart mutation and `addError` does not dedupe.
 * A per-group call also only sees the items of that group, so a parent living in
 * another group reads as an orphan there even though the flat pass resolves it —
 * event count therefore does not map one-to-one onto lost gifts. The
 * `PromotionType.TOTAL` key is skipped because those children are returned at root
 * rather than nested, so they are never orphans.
 */
const reportOrphanedFreegoods = (
  parentChildMapping: ParentChildMap,
  renderedProducts: ICartProduct[],
) => {
  const orphanedFreegoods = Object.entries(parentChildMapping)
    .filter(([parentKey]) => parentKey !== PromotionType.TOTAL)
    .flatMap(([parentKey, productsByPackage]) =>
      Object.entries(productsByPackage ?? {}).flatMap(
        ([packageKey, products]) =>
          (products ?? []).map(({ sku }) => ({ parentKey, packageKey, sku })),
      ),
    )

  if (orphanedFreegoods.length === 0) {
    return
  }

  datadogRum.addError(
    new Error('freegoods filed under a cart line that does not render'),
    generateDataDogContext({
      title: 'freegood not attached to a rendered cart line',
      extraInfo: {
        function: 'getCartProductsWithSubitems',
        orphanedFreegoods,
        // Capped deliberately: the browser SDK drops events over its size limit, so
        // an unbounded sku list would lose the signal on exactly the large carts
        // most likely to carry a complicated promotion. The orphan list above is the
        // actionable part; these are context for why no parent matched.
        renderedSkus: renderedProducts
          .slice(0, RENDERED_SKUS_REPORT_LIMIT)
          .map(({ sku }) => sku),
        renderedSkuCount: renderedProducts.length,
      },
    }),
  )
}

/**
 * Set of conditions for whether a promotion should generate item extras for UI (freegoods).
 */
const conditionsUsePromotionAsItemExtra: Condition<ActivePromotion>[] = [
  ({ freeGoods }) => Number(freeGoods?.length) > 0,
]

/**
 * Args shape for match-product-to-promotion mapping actions.
 */
interface IMatchProductPromotionArgs {
  activePromotionMatchProductAsFreegood?: boolean
  cartProduct: ICartProduct
  activePromotion: ActivePromotion
  parentChildMapping: ParentChildMap
  config?: IConfig
  /**
   * Required, not optional: parent resolution silently degrades to pre-fix
   * behavior if this ever fails to reach `getConsumedParent`, so let the compiler
   * enforce the thread rather than defaulting it away.
   */
  cartProducts: ICartProduct[]
}

/**
 * Defines how to map parents in the cart to their children (freegoods) based on promotion logic.
 * Supports the special GMV/Total Order promo, and regular child assignment by consumed SKU.
 */
const conditionsMapParents: IConditionAction<
  IMatchProductPromotionArgs,
  ParentChildMap
>[] = [
  // GMV/Total Order Promotion: Free items added at root under special prefix, readOnly for UI.
  {
    condition: ({
      activePromotion: {
        promotion: { type },
      },
      activePromotionMatchProductAsFreegood,
    }) =>
      !!activePromotionMatchProductAsFreegood && type === PromotionType.TOTAL,
    transformAction: ({
      activePromotion,
      cartProduct: { sku: cartProductSku },
      cartProduct,
      parentChildMapping,
      config,
    }) => {
      const {
        fields: { customerGetsQuantity, type },
        freeGoods,
      } = getActivePromotionsFields(activePromotion)
      const freegoods = freeGoods.filter(({ items }) =>
        items?.some(({ sku: currentSku }) => currentSku === cartProductSku),
      )
      const promotionalPrice = 0
      const promotionalQty = freegoods.reduce<number>(
        (accFreegoods, { items }) => {
          const totalItems =
            items?.reduce<number>(
              (accItems, { sku, quantity }) =>
                sku === cartProductSku ? accItems + (quantity || 0) : accItems,
              accFreegoods,
            ) || 0
          return totalItems
        },
        0,
      )
      const product: ICartProduct = {
        ...cartProduct,
        sku: `${PREFIX_GMV_FREE_PRODUCT}${cartProductSku}`,
        readOnly: true,
        quantity: promotionalQty,
        promotion: {
          value: promotionalPrice,
          ...promotionsMap[type](!!customerGetsQuantity)({
            activePromotion,
            cartProduct: {
              ...cartProduct,
              promotionalQty,
            },
            config,
          }),
        },
      }
      const existingChildren: ICartProduct[] =
        parentChildMapping[PromotionType.TOTAL]?.products || []

      return {
        ...parentChildMapping,
        [PromotionType.TOTAL]: { products: [ ...existingChildren, product ] },
      }
    },
  },
  // Regular freegoods: Add children to correct parent key and propName.
  {
    condition: ({ activePromotionMatchProductAsFreegood }) =>
      !!activePromotionMatchProductAsFreegood,
    transformAction: ({
      activePromotion,
      cartProduct,
      parentChildMapping,
      config,
      cartProducts,
    }) => {
      const {
        fields: { customerGetsQuantity, type },
        freeGoods,
      } = getActivePromotionsFields(activePromotion)
      const freegoods = freeGoods.filter(({ items }) =>
        items?.some((item) => isSkuQuantitySameAsProduct(item, cartProduct)))

      const result: ParentChildMap = freegoods.reduce<ParentChildMap>((acc, freegood) => {
        const freegoodItemQuantity =
          freegood.items?.find(
            (item) => isSkuQuantitySameAsProduct(item, cartProduct),
          )?.quantity || 0

        const promotionalQty = freegoodItemQuantity
        const product: ICartProduct = {
          ...cartProduct,
          readOnly: false,
          quantity: cartProduct.quantity,
          promotion: promotionsMap[type](!!customerGetsQuantity)({
            activePromotion,
            cartProduct: {
              ...cartProduct,
              promotionalQty,
            },
            config,
            cartProducts,
          }),
        }

        const consumedParent = getConsumedParent(freegood, cartProducts)

        if (!!consumedParent?.sku) {
          const { sku, packageName } = consumedParent
          const propForProducts = packageName ?? PRODUCTS_PROP_NAME
          const existingChildren: ICartProduct[] = acc[sku]?.[propForProducts] || []
          const newProductsParent: ParentChildMap = {
            ...acc,
            [sku]: {
              ...acc[sku],
              [propForProducts]: [...existingChildren, product],
            }
          }
          return newProductsParent
        }
        return acc
      }, parentChildMapping)
      return result
    },
  },
]

/**
 * Args for filtering freegood relationships from promotions.
 */
interface IFilterFreegoodsArgs {
  cartProduct: ICartProduct
  activePromotions: ActivePromotion[]
  parentChildMapping: ParentChildMap
  config?: IConfig
  cartProducts: ICartProduct[]
}

/**
 * Rules for determining which cart products are tied to which freegood (child items) via promotions.
 */
const conditionsFilterFreegoods: IConditionAction<
  IFilterFreegoodsArgs,
  ParentChildMap
>[] = [
  {
    condition: ({ cartProduct: { promotionalQty } }) => promotionalQty > 0,
    transformAction: ({
      cartProduct,
      activePromotions,
      parentChildMapping,
      config,
      cartProducts,
    }) =>
      activePromotions.reduce<ParentChildMap>((acc, activePromotion) => {
        const activePromotionMatchProductAsFreegood =
          !!activePromotion.freeGoods?.some(({ items }) =>
            items?.some(
              (item) => isSkuQuantitySameAsProduct(item, cartProduct),
            )
          )

        const validCondition = conditionsMapParents.find(({ condition }) =>
          condition({
            activePromotionMatchProductAsFreegood,
            cartProduct,
            activePromotion,
            parentChildMapping: acc,
            cartProducts,
          }),
        )
        return (
          validCondition?.transformAction({
            cartProduct,
            activePromotion,
            parentChildMapping: acc,
            config,
            cartProducts,
          }) || acc
        )
      }, parentChildMapping),
  },
]

/**
 * Builds a mapping of cart product parents to their freegood children according to all eligible promotions.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional platform/config options
 * @returns Parent/Child mapping where parent key is SKU (optionally with package), and children are freegood products
 */
export const mapFreegoodsAndParents = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
): ParentChildMap => {
  const activePromotionsFiltered = activePromotions.filter((activePromotion) =>
    conditionsUsePromotionAsItemExtra.every((condition) =>
      condition(activePromotion),
    ),
  )
  return cartProducts.reduce<ParentChildMap>(
    (acc, cartProduct) =>
      conditionsFilterFreegoods
        .find(({ condition }) =>
          condition({
            cartProduct,
            activePromotions: activePromotionsFiltered,
            parentChildMapping: acc,
            cartProducts,
          }),
        )
        ?.transformAction({
          cartProduct,
          activePromotions: activePromotionsFiltered,
          parentChildMapping: acc,
          config,
          cartProducts,
        }) || acc,
    {},
  )
}

/**
 * First-level product filter: only products with quantity > 0.
 */
const conditionsProductosFirstLevel: Condition<ICartProduct>[] = [
  ({ quantity }) => quantity > 0,
]

/**
 * Produces the list of cart products, each with possible `itemsExtra` sub-items
 * (the "freegoods" belonging to that cart item), based on current promotions.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional options/config
 * @returns List of cart products, with any attached sub-items for UI
 */
export const getCartProductsWithSubitems = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
): ICartProduct[] => {
  let parentChildMapping = mapFreegoodsAndParents(
    cartProducts,
    activePromotions,
    config,
  )

  // Optionally disables group display of freegoods, if feature flag set in config.options
  if (config?.options?.freegoodAsCartGroup) {
    parentChildMapping = {}
  }
  const freegoodsGMV: ICartProduct[] =
    parentChildMapping[PromotionType.TOTAL]?.[PRODUCTS_PROP_NAME] || []
  const filteredProducts = cartProducts
    .filter((cartProduct) =>
      conditionsProductosFirstLevel.every((condition) =>
        condition(cartProduct),
      ),
    )
    .map<ICartProduct>((cartProduct) => {
      const { sku: cartProductSku, package: cartProductPackage } = cartProduct
      const itemsExtraResultFreeGood = parentChildMapping[cartProductSku]?.[cartProductPackage?.name ?? PRODUCTS_PROP_NAME]
      // Remove child assignment after retrieving for one pass
      delete parentChildMapping[cartProductSku]?.[cartProductPackage?.name ?? PRODUCTS_PROP_NAME]
      const result: ICartProduct = {
        ...cartProduct,
        itemsExtra: itemsExtraResultFreeGood,
      }

      return result
    })
  reportOrphanedFreegoods(parentChildMapping, filteredProducts)
  return [...filteredProducts, ...freegoodsGMV]
}

/**
 * Generates a flattened, deduplicated list of all "freegood" products given by promotions.
 * Each freegood item in the output will have an empty description.
 *
 * @param cartProducts - Cart product array
 * @param activePromotions - List of active promotions
 * @param config - Optional config
 * @returns Flat array of freegood products, deduplicated by SKU & promotion
 */
export const getFreeGoodsList = (
  cartProducts: ICartProduct[],
  activePromotions: ActivePromotion[],
  config?: IConfig,
) => {
  const parentChildMapping = mapFreegoodsAndParents(
    cartProducts,
    activePromotions,
    config,
  )

  return Object.keys(parentChildMapping)
    .flatMap((skuKey) => Object.keys(parentChildMapping[skuKey] || {}).flatMap((packageKey) => parentChildMapping[skuKey]?.[packageKey] || []))
    .filter((item): item is ICartProduct => item !== undefined)
    .map((item) => ({ ...item, description: '' }))
    .reduce<ICartProduct[]>((acc, item) => {
      if (acc.find((i) => i.sku === item.sku && item.promotion)) {
        // If there's a duplicate freegood for this SKU, merge promo origins.
        return acc.map((currItem) => {
          if (currItem.sku === item.sku) {
            const promotion = item.promotion
            const promoOrigin = promotion?.promoOrigin ?? []
            const currentPromoOrigin = currItem.promotion?.promoOrigin ?? []
            const newItem = {
              ...item,
              promotion: {
                ...promotion,
                promoOrigin: [...currentPromoOrigin, ...promoOrigin],
              },
            }
            return newItem
          }
          return currItem
        })
      }
      return [...acc, item]
    }, [])
}

/**
 * Filters out all products from the cart that are "freegoods" (awarded via promotion),
 * except for items with promotionalQty > 1 (for some edge-cases).
 *
 * @param params.cartProducts - The cart product array
 * @param params.activePromotions - Promotions active now
 * @returns Array of non-freegood items
 */
export const getCartWithoutFreegoods = ({
  cartProducts,
  activePromotions,
}: ITransformCartProductsParams): ICartProduct[] => {
  return cartProducts.filter((cartProduct) => {
    const activePromotionMatchProductAsFreegood = activePromotions.some(
      ({ freeGoods }) =>
        !!freeGoods?.some(({ items }) =>
          items?.some(
            (item) => isSkuQuantitySameAsProduct(item, cartProduct),
          )
        ),
    )
    return (
      !activePromotionMatchProductAsFreegood ||
      (activePromotionMatchProductAsFreegood && cartProduct.promotionalQty > 1)
    )
  })
}

/**
 * Main function for UI:
 * Transforms the input cart product list to include all needed promotion information, including
 * freegood/product subitems (`itemsExtra`) attached to cart products, and mapping all
 * relevant promotions to the right products for subitem UIs.
 *
 * 1. Filters out promotions that are only "subitem" (freegood) type,
 *    to focus on top-level promotions only.
 * 2. Attaches subitems to cart products (each with relevant promotion object).
 * 3. Reduces through top-level promotions, using a matching transformer for each promo type,
 *    to finalize UI-useful product structure.
 *
 * @param params.cartProducts - The cart product array
 * @param params.activePromotions - All currently active promotions
 * @param params.config - Optional config
 * @returns The transformed array of products, shaped for UI display with subitems and promotion info.
 */
export const setPromotionUiDetailsCartSubitems = ({
  cartProducts,
  activePromotions,
  config,
}: ITransformCartProductsParams): ICartProduct[] => {
  // Only keep top-level promotions (not freegood-only)
  const activePromotionsFiltered = activePromotions.filter(
    (activePromotion) =>
      !conditionsUsePromotionAsItemExtra.every((condition) =>
        condition(activePromotion),
      ),
  )

  const cartProductsWithSubitems = getCartProductsWithSubitems(
    cartProducts,
    activePromotions,
    config,
  )
  const result = activePromotionsFiltered?.reduce<ICartProduct[]>(
    (currentItems, activePromotion) => {
      const activePromotionFields = getActivePromotionsFields(activePromotion)
      return currentItems.map(
        (cartProduct) =>
          transformProductArray
            .find(({ condition }) =>
              condition({
                activePromotionFields,
                cartProduct,
                activePromotion,
                config,
                activePromotions: activePromotionsFiltered,
              }),
            )
            ?.transformAction({
              activePromotionFields,
              cartProduct,
              activePromotion,
              config,
              activePromotions: activePromotionsFiltered,
            }) || cartProduct,
      )
    },
    cartProductsWithSubitems,
  )
  return result
}
